import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  Person,
  Business,
  Work,
  AccountBalance,
  AttachMoney,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import Card from "../../../components/ui/Card";

const currency = (value) => {
  const num = Number(value || 0);
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function SalarySlipSummaryCard({ salarySlip }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card>
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-primary mb-1">
            Salary Slip — {salarySlip.month}
          </h2>
          <p className="text-sm text-gray-500">Payment details and breakdown</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Employee Info */}
          <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl border border-primary/20">
            <div className="flex items-center gap-2 mb-3">
              <Person className="w-5 h-5 text-primary" />
              <label className="text-sm font-semibold text-gray-700">
                Employee
              </label>
            </div>
            <h3 className="text-lg font-bold text-primary mb-1">
              {salarySlip.employee_name}
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              {salarySlip.designation}
            </p>
            <span className="inline-block px-3 py-1 bg-primary text-white text-xs font-semibold rounded-full">
              {salarySlip.department}
            </span>
          </div>

          {/* Bank Details */}
          <div className="p-4 bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-2xl border border-secondary/20">
            <div className="flex items-center gap-2 mb-3">
              <AccountBalance className="w-5 h-5 text-secondary" />
              <label className="text-sm font-semibold text-gray-700">
                Bank Details
              </label>
            </div>
            <p className="text-sm text-gray-700 mb-1">
              <span className="font-medium">Account:</span>{" "}
              {salarySlip.bank_account_no}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">IFSC:</span> {salarySlip.ifsc_code}
            </p>
          </div>

          {/* Net Salary */}
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
            <div className="flex items-center gap-2 mb-3">
              <AttachMoney className="w-5 h-5 text-green-600" />
              <label className="text-sm font-semibold text-gray-700">
                Net Salary
              </label>
            </div>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600 break-words">
              {currency(salarySlip.net_salary)}
            </p>
          </div>
        </div>

        {/* Earnings & Deductions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
          {/* Earnings */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-bold text-primary">Earnings</h3>
            </div>
            <div className="space-y-3">
              <SummaryLine
                label="Basic Salary"
                value={salarySlip.basic_salary}
              />
              <SummaryLine label="HRA" value={salarySlip.hra} />
              <SummaryLine label="Allowances" value={salarySlip.allowances} />
              <SummaryLine
                label="Overtime"
                value={salarySlip.overtime_amount}
              />
              <div className="pt-3 mt-3 border-t border-gray-200">
                <SummaryLine
                  label="Gross Salary"
                  value={salarySlip.gross_salary}
                  highlight
                />
              </div>
            </div>
          </div>

          {/* Deductions */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-bold text-primary">Deductions</h3>
            </div>
            <div className="space-y-3">
              <SummaryLine
                label="Professional Tax"
                value={salarySlip.professional_tax}
              />
              <SummaryLine
                label="PF Contribution"
                value={salarySlip.pf_contribution}
              />
              <SummaryLine label="ESI" value={salarySlip.esi} />
              <SummaryLine label="TDS" value={salarySlip.tds} />
              <div className="pt-3 mt-3 border-t border-gray-200">
                <SummaryLine
                  label="Total Deductions"
                  value={salarySlip.total_deductions}
                  highlight
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function SummaryLine({ label, value, highlight = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex items-center justify-between p-3 rounded-xl ${
        highlight
          ? "bg-gradient-to-r from-primary/10 to-secondary/10 border-2 border-primary/20"
          : "bg-gray-50"
      }`}
    >
      <p
        className={`text-sm font-medium ${
          highlight ? "text-primary font-bold" : "text-gray-700"
        }`}
      >
        {label}
      </p>
      <p
        className={`text-base font-bold ${
          highlight ? "text-primary" : "text-gray-800"
        }`}
      >
        {currency(value)}
      </p>
    </motion.div>
  );
}

SummaryLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  highlight: PropTypes.bool,
};

SalarySlipSummaryCard.propTypes = {
  salarySlip: PropTypes.shape({
    month: PropTypes.string.isRequired,
    employee_name: PropTypes.string.isRequired,
    designation: PropTypes.string,
    department: PropTypes.string,
    bank_account_no: PropTypes.string,
    ifsc_code: PropTypes.string,
    basic_salary: PropTypes.number,
    hra: PropTypes.number,
    allowances: PropTypes.number,
    overtime_amount: PropTypes.number,
    gross_salary: PropTypes.number,
    professional_tax: PropTypes.number,
    pf_contribution: PropTypes.number,
    esi: PropTypes.number,
    tds: PropTypes.number,
    total_deductions: PropTypes.number,
    net_salary: PropTypes.number,
  }).isRequired,
};

export default SalarySlipSummaryCard;
